import {
  CalendarEvent,
  Task,
  TaskType,
  Timestamp,
  Todo,
  WeekDays,
} from '@griply/types';
import { addMilliseconds, endOfDay, format, startOfDay } from 'date-fns';

import { HALF_HOUR } from './constants';
import { formatIdDateKey } from './format-id-date-key';
import {
  getCalendarTaskEndDate,
  getCalendarTaskStartDate,
} from './get-calendar-task-dates';
import { getTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';
import { getTimeframeSchedules } from './schedules';

export type GetTasksCalendarEventsOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  hideCompleted?: boolean;
};

const getTaskMeta = (
  task: Task,
): Pick<
  CalendarEvent,
  'taskId' | 'actionType' | 'actionMetaId' | 'goalId' | 'lifeAreaId'
> => ({
  taskId: task.id,
  actionType: task.action?.type,
  actionMetaId: task.action?.meta?.id ?? undefined,
  goalId: task.goalId ?? undefined,
  lifeAreaId: task.lifeAreaId ?? undefined,
});

const getRegularTaskEvents = (
  task: Task,
  { hideCompleted }: Pick<GetTasksCalendarEventsOptions, 'hideCompleted'>,
): CalendarEvent[] => {
  if (hideCompleted && task.completedAt) {
    return [];
  }

  const events: CalendarEvent[] = [];
  const meta = getTaskMeta(task);
  const start = getCalendarTaskStartDate(task);
  const end = getCalendarTaskEndDate(task);
  const hasStart = isNumber(start);

  // allDay
  if (!hasStart) {
    const deadline = task.endStrategy?.deadline;
    if (deadline) {
      const dateStr = format(deadline, 'yyyy-MM-dd');
      events.push({
        id: task.id,
        title: task.name,
        start: { date: dateStr },
        end: { date: dateStr },
        allDay: true,
        date: deadline,
        duration: (task as Todo).timeslot?.duration || HALF_HOUR,
        ...meta,
      });
    }
  }

  // timeslot
  if (hasStart && isNumber(end)) {
    events.push({
      id: task.id,
      title: task.name,
      start: { dateTime: start.toISOString() },
      end: { dateTime: end.toISOString() },
      allDay: false,
      date: task.endStrategy?.deadline ?? undefined,
      duration: (task as Todo).timeslot?.duration || HALF_HOUR,
      ...meta,
    });
  }

  // Deadline marker (separate all-day event)
  if (task.deadlineDeadline) {
    const deadlineId = formatIdDateKey(task.id, task.deadlineDeadline);
    events.push({
      id: deadlineId,
      title: task.name,
      start: { date: format(task.deadlineDeadline, 'yyyy-MM-dd') },
      end: { date: format(task.deadlineDeadline, 'yyyy-MM-dd') },
      allDay: true,
      date: task.deadlineDeadline,
      isDeadline: true,
      ...meta,
    });
  }

  return events;
};

const getHabitTaskEvents = (
  task: Task,
  {
    startDate,
    endDate,
    today,
    weekStartsOn,
    hideCompleted,
  }: GetTasksCalendarEventsOptions,
): CalendarEvent[] => {
  if (!task.schedules?.length || task.hideOnTodo) {
    return [];
  }

  const timeframeSchedules = getTimeframeSchedules(task.schedules, {
    startDate,
    endDate,
  });
  if (!timeframeSchedules.length) {
    return [];
  }

  const plannedOccurrences = getTaskPlanning(task, {
    weekStartsOn,
    startDate: startOfDay(startDate),
    endDate: endOfDay(endDate),
    today,
    uncompletedOnly: hideCompleted,
  });
  if (!plannedOccurrences.length) {
    return [];
  }

  const events: CalendarEvent[] = [];
  const meta = getTaskMeta(task);

  for (const occurrence of plannedOccurrences) {
    const duration = occurrence.timeslot?.duration ?? HALF_HOUR;
    const hasTime = isNumber(occurrence.timeslot?.startTime);

    const occStart = hasTime
      ? addMilliseconds(
          startOfDay(occurrence.date),
          occurrence.timeslot!.startTime!,
        )
      : undefined;
    const occEnd = occStart ? addMilliseconds(occStart, duration) : undefined;

    const eventId = formatIdDateKey(
      task.id,
      occStart ?? occurrence.date,
      duration,
    );

    // timeslot
    if (hasTime && occStart && occEnd) {
      events.push({
        id: eventId,
        title: task.name,
        start: { dateTime: occStart.toISOString() },
        end: { dateTime: occEnd.toISOString() },
        allDay: false,
        date: occurrence.date,
        occurrenceId: occurrence.id,
        duration,
        ...meta,
      });
      continue;
    }

    // allDay
    const dateStr = format(occurrence.date, 'yyyy-MM-dd');
    events.push({
      id: eventId,
      title: task.name,
      start: { date: dateStr },
      end: { date: dateStr },
      allDay: true,
      date: occurrence.date,
      occurrenceId: occurrence.id,
      duration,
      ...meta,
    });
  }

  return events;
};

export const getTasksCalendarEvents = (
  tasks: Task[],
  options: GetTasksCalendarEventsOptions,
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];

  for (const task of tasks) {
    if ([TaskType.Habit, TaskType.Repeating].includes(task.type)) {
      events.push(...getHabitTaskEvents(task, options));
      continue;
    }

    events.push(...getRegularTaskEvents(task, options));
  }

  return events;
};
